import { apiBaseUrl } from 'app/src/msw/companyagreement/api/apiBaseUrl'
import { getAllStaticProductsCollection } from 'app/src/msw/companyagreement/initialSeed/products'
import type { companyagreementApi } from 'app/src/services/welfareportal'
import type { Product } from 'app/src/services/welfareportal/companyagreement-api'
import { http, HttpResponse, type DefaultBodyType } from 'msw'

/**
 * Handles GET requests to the `/products` endpoint by retrieving products from the `productCollection`
 * @returns A paginated list of products that match the query parameters, or an empty list if no such products exist
 */
export const getProducts = http.get<object, DefaultBodyType, companyagreementApi.ProductPaginated>(
  apiBaseUrl + '/products',
  async ({ request }) => {
    // set of request parameters
    const url = new URL(request.url)
    const key = url.searchParams.get('key')
    const limit: number = Number.parseInt(url.searchParams.get('pagingLimit') ?? '10')
    const offset: number = Number.parseInt(url.searchParams.get('pagingOffset') ?? '0')
    const onlyFavourites: boolean = Boolean(url.searchParams.get('onlyFavourites') === 'true')
    const categoryId = url.searchParams.get('categoryId') ?? ''
    const kind = url.searchParams.get('serviceCategory') ?? ''
    const parentCategoryId = url.searchParams.get('parentCategoryId') ?? ''
    const catalog = url.searchParams.get('catalog') === 'discount'

    console.log('Received request for getProducts with params:', {
      key,
      limit,
      offset,
      onlyFavourites,
      categoryId,
      kind,
      parentCategoryId,
      catalog,
    })

    let productCollection: Product[] = await getAllStaticProductsCollection()

    // empty response
    const emptyResponse: companyagreementApi.ProductPaginated = { paging: { limit, offset, total: 0 }, items: [] }

    /**
     * Converts an array of products into a paginated response.
     * @param products The array of products to paginate.
     * @returns A paginated response containing the products.
     */
    const convertToPaginatedResponse = (products: companyagreementApi.Product[]): companyagreementApi.ProductPaginated => ({
      paging: { limit, offset, total: products.length },
      items: products
        .map((p) => ({
          productId: p.productId,
          productName: p.productName,
          description: p.description,
          mainImage: p.mainImage,
          geoLocation: p.geoLocation,
          geoAddress: p.geoAddress,
          weight: p.weight,
          isFavourite: p.isFavourite,
          additionalInfo: p.additionalInfo,
        }))
        .slice(offset, offset + limit),
    })

    // If no name is provided and the category is either 'DIRECT' or 'DISCOUNT', return an empty response
    if (!key && (categoryId === 'DIRECT' || categoryId === 'DISCOUNT')) {
      return HttpResponse.json(emptyResponse)
    }

    // if name is present add to where clause
    if (key) {
      productCollection = productCollection.filter(
        (product: Product) =>
          product.productName?.toLowerCase().includes(key.toLowerCase()) ||
          product.description?.toLowerCase().includes(key.toLowerCase()),
      )
    }

    // if onlyFavourites is true, add to where clause and return the filtered products
    if (onlyFavourites === true) {
      productCollection = productCollection.filter((product: Product) => product.isFavourite === true)
      const response = convertToPaginatedResponse(productCollection)
      return HttpResponse.json(response)
    }

    // if kind is provided and is either 'welfareArt100', 'welfareArt51' or 'none', add to where clause
    if (kind && (kind === 'welfareArt100' || kind === 'welfareArt51' || kind === 'none')) {
      productCollection = productCollection.filter((product: Product) => product.serviceCategory === kind)
    }

    // if categoryId is main category filter by typology to reduce the initial query result
    if (categoryId === 'DISCOUNT') {
      productCollection = productCollection.filter((product: Product) => product.typology === 'discount')
    } else if (categoryId === 'TRANSFER') {
      productCollection = productCollection.filter((product: Product) => product.typology === 'moneyTransfer')
    } else if (categoryId === 'REFUND') {
      productCollection = productCollection.filter((product: Product) => product.typology === 'refund')
    }

    // if categoryId is provided, filter the products by categoryId. This is done after the initial query to reduce the number of products to filter in case of main categories
    if (categoryId) {
      productCollection = productCollection.filter((product: Product) =>
        product.categoryIds?.some((id) => id.startsWith(categoryId)),
      )
    }

    // if no products are found, return an empty response
    if (!productCollection.length) {
      return HttpResponse.json(emptyResponse)
    } else {
      const response = convertToPaginatedResponse(productCollection)
      return HttpResponse.json(response)
    }
  },
)
